/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useEffect, useCallback, useMemo } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { Label } from "../types/label";
import * as c from "../constants";
import { WorldDataFeature } from "../types/worldData";
import { getColour } from "../utils/getColor";
import { buildTransactionLabel } from "../utils/buildTransactionLabel";
import { sunPosAt } from "../utils/sun";
import { useTransactionStream } from "../hooks/useTransactionStream";
import Overlays from "./Overlays";

// --- Day/night terminator shader: blends the day texture with the night-lights
// texture based on the sun's direction relative to each point on the globe. ---
const dayNightVertexShader = `
  varying vec3 vNormal;
  varying vec2 vUv;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const dayNightFragmentShader = `
  #define PI 3.141592653589793
  uniform sampler2D dayTexture;
  uniform sampler2D nightTexture;
  uniform vec2 sunPosition;
  uniform vec2 globeRotation;
  varying vec3 vNormal;
  varying vec2 vUv;

  float toRad(in float a) { return a * PI / 180.0; }

  vec3 Polar2Cartesian(in vec2 c) { // [lng, lat]
    float theta = toRad(90.0 - c.x);
    float phi = toRad(90.0 - c.y);
    return vec3(sin(phi) * cos(theta), cos(phi), sin(phi) * sin(theta));
  }

  void main() {
    float invLon = toRad(globeRotation.x);
    float invLat = -toRad(globeRotation.y);
    mat3 rotX = mat3(1, 0, 0, 0, cos(invLat), -sin(invLat), 0, sin(invLat), cos(invLat));
    mat3 rotY = mat3(cos(invLon), 0, sin(invLon), 0, 1, 0, -sin(invLon), 0, cos(invLon));
    vec3 rotatedSunDirection = rotX * rotY * Polar2Cartesian(sunPosition);
    float intensity = dot(normalize(vNormal), normalize(rotatedSunDirection));
    vec4 dayColor = texture2D(dayTexture, vUv);
    vec4 nightColor = texture2D(nightTexture, vUv) * 0.9;
    float blendFactor = smoothstep(-0.15, 0.15, intensity);
    gl_FragColor = mix(nightColor, dayColor, blendFactor);
  }
`;

export const WorldGlobe = () => {
  const { transactionCount, arcsData, ringsData, labelData } = useTransactionStream();
  const GLOBE_SPEED = 1;
  const globeEl = useRef<any>();

  // Day/night globe material
  const globeMaterial = useMemo(() => {
    const loader = new THREE.TextureLoader();
    const dayTexture = loader.load("./img/earth_8k.jpg");
    const nightTexture = loader.load("./img/earth_night.jpg");
    dayTexture.colorSpace = THREE.SRGBColorSpace;
    nightTexture.colorSpace = THREE.SRGBColorSpace;
    return new THREE.ShaderMaterial({
      uniforms: {
        dayTexture: { value: dayTexture },
        nightTexture: { value: nightTexture },
        sunPosition: { value: new THREE.Vector2(...sunPosAt(new Date())) },
        globeRotation: { value: new THREE.Vector2() },
      },
      vertexShader: dayNightVertexShader,
      fragmentShader: dayNightFragmentShader,
    });
  }, []);

  useEffect(() => {
    const globe = globeEl.current as any;
    if (!globe) return;

    globe.pointOfView(c.MAP_CENTER);
    globe.controls().autoRotate = true;
    globe.controls().autoRotateSpeed = GLOBE_SPEED;

    let frameId = 0;
    let clouds: THREE.Mesh | null = null;

    // Defer until the globe's scene + radius are initialised
    const setupTimer = setTimeout(() => {
      const directionalLight = globe
        .scene()
        .children.find((obj3d: any) => obj3d.type === "DirectionalLight");
      if (directionalLight) {
        directionalLight.intensity = 0.5;
      }

      // Rotating cloud layer
      const radius = globe.getGlobeRadius();
      const CLOUDS_ALT = 0.006;
      const CLOUDS_ROTATION_SPEED = 0.004; // deg per frame
      new THREE.TextureLoader().load("./img/clouds.png", (cloudsTexture) => {
        cloudsTexture.colorSpace = THREE.SRGBColorSpace;
        clouds = new THREE.Mesh(
          new THREE.SphereGeometry(radius * (1 + CLOUDS_ALT), 75, 75),
          new THREE.MeshPhongMaterial({ map: cloudsTexture, transparent: true, opacity: 0.9 })
        );
        globe.scene().add(clouds);
        const rotateClouds = () => {
          if (clouds) clouds.rotation.y += (CLOUDS_ROTATION_SPEED * Math.PI) / 180;
          frameId = requestAnimationFrame(rotateClouds);
        };
        rotateClouds();
      });
    }, 100);

    return () => {
      clearTimeout(setupTimer);
      cancelAnimationFrame(frameId);
      if (clouds) {
        globe.scene().remove(clouds);
        clouds.geometry.dispose();
        (clouds.material as THREE.Material).dispose();
      }
    };
  }, []);

  // Advance the sun position over time so the night side tracks real time
  useEffect(() => {
    const update = () => {
      globeMaterial.uniforms.sunPosition.value.set(...sunPosAt(new Date()));
    };
    update();
    const interval = setInterval(update, 10000);
    return () => clearInterval(interval);
  }, [globeMaterial]);

  const handlePoligonColor = useCallback((k: any) => {
    const data = k as WorldDataFeature;
    return data.properties.colour;
  }, []);

  const handleHtmlElement = (d: object) => {
    const { html, className } = buildTransactionLabel(d as Label);
    const el = document.createElement("div");
    el.innerHTML = html;
    el.className = className;
    return el;
  };

  const handleArcColour = useCallback((arc: any) => {
    return [getColour(arc.type, true), getColour(arc.type)];
  }, []);

  const handleRingColour = useCallback(() => `#73f87e`, []);

  const handleZoom = useCallback(
    (pov: { lng: number; lat: number }) => {
      globeMaterial.uniforms.globeRotation.value.set(pov.lng, pov.lat);
    },
    [globeMaterial]
  );

  return (
    <>
      <Overlays transactionCount={transactionCount} />
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
      }}>
        <Globe
          showAtmosphere
          globeMaterial={globeMaterial}
          atmosphereColor="white"
          backgroundImageUrl="./img/night.png"
          hexPolygonResolution={3}
          hexPolygonMargin={0.2}
          hexPolygonColor={handlePoligonColor}
          atmosphereAltitude={0.1}
          htmlElementsData={labelData}
          htmlElement={handleHtmlElement}
          htmlTransitionDuration={0}
          onZoom={handleZoom}
          ref={useCallback<any>(globeEl, [])}
          arcsData={arcsData}
          arcColor={handleArcColour}
          arcDashLength={c.ARC_REL_LEN}
          arcDashGap={1.5}
          arcDashInitialGap={0.5}
          arcDashAnimateTime={c.FLIGHT_TIME}
          arcsTransitionDuration={0}
          ringsData={ringsData}
          arcStroke={3.5}
          ringColor={handleRingColour}
          ringMaxRadius={c.RINGS_RADIUS_SIZE}
          ringPropagationSpeed={c.RING_PROPAGATION_SPEED}
          ringRepeatPeriod={(c.FLIGHT_TIME * c.ARC_REL_LEN) / c.NUM_RINGS}
        />
      </div>
    </>
  );
};
